import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { INFO_ITEMS } from '@/utils/data/infos';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { useRef } from 'react';

function InfoDescription({ description, highlight }: { description: string; highlight: string }) {
  const [before, after] = description.split(highlight);

  return (
    <>
      <p className="sr-only">
        {before}
        {highlight}
        {after}
      </p>

      <p aria-hidden="true">
        {before}
        <span className="font-bold">{highlight}</span>
        {after}
      </p>
    </>
  );
}

export default function Infos() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const blockRefs = useRef<HTMLDivElement[]>([]);

  useGSAP(() => {
    if (!sectionRef.current || !titleRef.current) return;

    const splitTitle = new SplitText(titleRef.current, {
      mask: 'lines',
      type: 'lines',
    });

    const blockSplits = blockRefs.current.map((block) => {
      const title = block.querySelector('h3');
      const paragraph = block.querySelector('p[aria-hidden="true"]');
      const icon = block.querySelector('img');

      if (!title || !paragraph || !icon) return null;

      const splitBlockTitle = new SplitText(title, {
        mask: 'lines',
        type: 'lines',
      });
      const splitParagraph = new SplitText(paragraph, {
        mask: 'lines',
        type: 'lines',
      });

      return {
        icon,
        splits: [splitBlockTitle, splitParagraph],
      };
    });

    const tl = gsap.timeline({
      defaults: {
        duration: MOTION.durationMd,
        ease: MOTION.ease,
      },
      scrollTrigger: {
        trigger: sectionRef.current,
        start: 'top 60%',
        // markers: true,
        toggleActions: 'play none none reverse',
      },
    });

    tl.from(splitTitle.lines, {
      stagger: MOTION.staggerLines,
      yPercent: MOTION.yPercent,
    });

    blockSplits.forEach((block) => {
      if (!block) return;

      tl.from(
        block.icon,
        {
          autoAlpha: 0,
          scale: 0.8,
          y: MOTION.y,
        },
        '<+0.15',
      ).from(
        block.splits.flatMap((split) => split.lines),
        {
          stagger: MOTION.staggerLines,
          yPercent: MOTION.yPercent,
        },
        '<',
      );
    });
  });

  return (
    <section ref={sectionRef} className="max-width-white-section">
      <div className="max-width-white-section-content container-infos flex flex-col items-center gap-8">
        <h2 ref={titleRef} className="text-orange text-center">
          Une réponse simple à vos questions
        </h2>

        <div className="grid items-center gap-8 md:grid-cols-2 md:px-8">
          {INFO_ITEMS.map((item, index) => (
            <div
              key={item.title}
              ref={(element) => {
                if (element) blockRefs.current[index] = element;
              }}
              className="flex h-full flex-col gap-4"
            >
              <div className="flex max-h-14 items-center justify-start gap-4">
                <Image
                  alt="iconInfo"
                  className="h-6 w-auto"
                  height={24}
                  loading="lazy"
                  src={withBasePath('/images/logos/infoIcon.svg')}
                  width={24}
                />
                <h3>{item.title}</h3>
              </div>

              <InfoDescription description={item.description} highlight={item.highlight} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
